// The session's workspace in miniature, on the trace page: its card as the
// workspace's board draws it (what it was, where it ran, how the review found
// it, its lanes across the whole trace), nothing laid beside it, and one
// click opening the workspace, as the reading switcher's Workspace button
// does.
import { memo, useEffect, useMemo, useRef, useState } from "react";
import SessionCanvasView from "./SessionCanvasView.jsx";
import { turnsToJoinInputs } from "./TraceLanesView.jsx";
import { buildTraceLanes, isConversationDomain, lanesAtTurn } from "../lib/traceLanes.js";
import { readShellWork } from "../lib/traceShellWork.js";
import { recordTurnBase } from "../lib/traceTurnFocus.js";

// The groups holding a lane some pin is on, or null when none does.
export function pinnedGroups(model, pins) {
  if (!pins?.length) return null;
  const lanes = new Set(pins.map((p) => p.laneId));
  const groups = (model?.groups || []).filter((g) => (g.lanes || []).some((l) => lanes.has(l.id))).map((g) => g.id);
  return groups.length ? new Set(groups) : null;
}

export function preferredLane(lanes, prefer) {
  const talk = lanes.filter((l) => isConversationDomain(l.domain));
  const work = lanes.filter((l) => !isConversationDomain(l.domain));
  const pool = prefer === "work" ? (work.length ? work : talk) : (talk.length ? talk : work);
  return pool.slice(0, 1);
}

// `review` false leaves the review's findings off the card, for a host that
// lists them itself. With no `onOpen` a click does nothing: a host that draws
// over the card (`onAnchors`, told where its pins sit) takes the clicks.
// `lens`: { t0, t1 }, a stretch the card opens zoomed into. `onlyPinned`: the
// card keeps the rows its pins are on, the rest folded into one.
function WorkspaceCardPreview({ trace, turns, onOpen = null, pins = null, review = true, onAnchors = null, lens = null, onlyPinned = false }) {
  // The shell calls' file work joins the card's lanes once read, as it joins
  // the workspace's.
  const [shell, setShell] = useState(null);
  useEffect(() => {
    const ctl = new AbortController();
    setShell(null);
    readShellWork(turns, { signal: ctl.signal }).then(
      (r) => { if (!ctl.signal.aborted) setShell(r?.items || null); },
      () => {},
    );
    return () => ctl.abort();
  }, [turns]);
  const model = useMemo(() => buildTraceLanes(turns, shell?.length ? { extraItems: shell } : {}), [turns, shell]);
  // A pin marks a turn on every lane that did something at it.
  // A pin that names a `prefer` ("work" or "talk") marks one lane only: the
  // first of that kind at its turn, else the first of the other.
  const focalPins = useMemo(() => (pins?.length
    ? pins.flatMap((p) => {
      const { turn, lanes } = lanesAtTurn(model, p.turn);
      return (p.prefer ? preferredLane(lanes, p.prefer) : lanes).map((l) => ({ ...p, turn, laneId: l.id }));
    })
    : null), [pins, model]);
  const openRef = useRef(onOpen);
  openRef.current = onOpen;
  const only = useMemo(() => (onlyPinned ? pinnedGroups(model, focalPins) : null), [onlyPinned, focalPins, model]);
  const opens = !!onOpen;
  const preview = useMemo(() => ({ onOpen: opens ? () => openRef.current?.() : null, review, onAnchors, lens, only }), [opens, review, onAnchors, lens, only]);
  return (
    <SessionCanvasView
      trace={trace}
      turns={turns}
      model={model}
      turnsToJoinInputs={turnsToJoinInputs}
      preview={preview}
      turnBase={recordTurnBase(trace?.record)}
      focalRecord={trace?.record || null}
      focalPins={focalPins}
    />
  );
}

// The trace page renders often (a turn picked, the column dragged); the
// preview draws again only for another trace, other turns or other pins. Its open
// handler is read when clicked, so a new one each render changes nothing.
export default memo(WorkspaceCardPreview, (a, b) => a.trace === b.trace && a.turns === b.turns && a.pins === b.pins && a.review === b.review && !a.onOpen === !b.onOpen && a.onAnchors === b.onAnchors && a.lens?.t0 === b.lens?.t0 && a.lens?.t1 === b.lens?.t1 && a.onlyPinned === b.onlyPinned);
